import { useEffect, useRef } from 'react';
import { useBrandSettings } from '@/hooks/useBrandSettings';

/**
 * Hook que injeta as imagens de branding no splash HTML estático
 * e controla a remoção do splash após o branding ser injetado.
 */
export const useInjectBrandingIntoSplash = () => {
  const { data: brandSettings, isLoading } = useBrandSettings();
  const injectedRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (injectedRef.current) return;

    const brandingContainer = document.getElementById('splash-branding');
    if (!brandingContainer) return;

    injectedRef.current = true;

    // Limpar conteúdo anterior
    brandingContainer.innerHTML = '';

    const symbolUrl = brandSettings?.symbolDark;
    const logoTextUrl = brandSettings?.logoTextDark;
    const platformName = brandSettings?.platformName || 'AutoFLUXO';

    // Se tem símbolo, criar elemento de imagem
    if (symbolUrl) {
      const symbolImg = document.createElement('img');
      symbolImg.src = symbolUrl;
      symbolImg.alt = platformName;
      symbolImg.style.cssText = 'height: 64px; width: 64px; object-fit: contain;';
      symbolImg.onerror = () => symbolImg.remove();
      brandingContainer.appendChild(symbolImg);
    }

    // Se tem logo com texto, criar elemento
    if (logoTextUrl) {
      const logoImg = document.createElement('img');
      logoImg.src = logoTextUrl;
      logoImg.alt = platformName;
      logoImg.style.cssText = 'height: 40px; object-fit: contain; margin-top: 8px;';
      logoImg.onerror = () => logoImg.remove();
      brandingContainer.appendChild(logoImg);
    }

    // Se não tem nenhuma imagem, mostrar fallback
    if (!symbolUrl && !logoTextUrl) {
      showFallback(brandingContainer, platformName);
    }

    // Remover splash após branding injetado (dar tempo para imagens carregarem)
    setTimeout(() => {
      const htmlSplash = document.getElementById('initial-splash');
      if (htmlSplash) {
        htmlSplash.style.opacity = '0';
        setTimeout(() => htmlSplash.remove(), 300);
      }
    }, 800);
  }, [brandSettings, isLoading]);
};

function showFallback(container: HTMLElement, platformName: string) {
  container.innerHTML = `
    <div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E30613" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2"/>
        <circle cx="7" cy="17" r="2"/>
        <circle cx="17" cy="17" r="2"/>
      </svg>
      <span style="font-size: 20px; font-weight: bold; color: white;">${platformName}</span>
    </div>
  `;
}

export default useInjectBrandingIntoSplash;
